import React, { useRef, useState } from "react";
import type { BrandDefinition } from "@school-sales-ai/shared";
import { brandMarkSrc } from "@/lib/brand";

/**
 * The school's institutional logo — shown as the assistant's avatar in the widget
 * and as the mark at the top-left of the CRM.
 *
 * Deliberately breaks the `{ value, onChange, invalid }` contract the sibling
 * settings sections use: a multipart upload cannot ride the page's dirty/save
 * cycle, so this section commits immediately. Same reason DocumentsManager sits
 * outside the settings form.
 */
const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp"];

type Props = {
  logoUrl: string | null;
  /** The client's brand — its mark is what shows when no logo is uploaded. */
  brand: BrandDefinition;
  /**
   * The server's own cap (`BRANDING_MAX_FILE_SIZE_MB`), from GET /crm/settings.
   * Undefined while settings load — the check is then skipped and the backend
   * stays the only authority, which it is regardless: this is a fast, readable
   * error, never a gate.
   */
  maxSizeMb: number | undefined;
  onUpload: (file: File) => void;
  onRemove: () => void;
  isUploading: boolean;
  isRemoving: boolean;
};

export function LogoSettings({
  logoUrl,
  brand,
  maxSizeMb,
  onUpload,
  onRemove,
  isUploading,
  isRemoving,
}: Props): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const busy = isUploading || isRemoving;

  function handleFile(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    // Reset immediately so picking the same file twice still fires a change.
    e.target.value = "";
    if (!file) return;

    // Mirrored client-side for a fast, readable error. The backend re-checks both
    // and additionally sniffs the magic bytes — this is convenience, not a gate.
    // It also spares the admin a raw 413: @fastify/multipart aborts an oversize
    // upload before our own Spanish message can be reached.
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError("Formato no permitido. Usa PNG, JPG o WEBP.");
      return;
    }
    if (maxSizeMb !== undefined && file.size > maxSizeMb * 1024 * 1024) {
      setError(`La imagen supera el límite de ${maxSizeMb} MB.`);
      return;
    }
    setError(null);
    onUpload(file);
  }

  return (
    <div className="border-t border-ink-200 pt-4 mt-4">
      <div className="mb-1">
        <p className="text-[13px] font-medium text-ink-900">Imagen institucional</p>
        <p className="text-[12px] text-ink-500 mt-0.5">
          El logo de tu institución. Aparece como avatar del asistente en el chat y
          arriba a la izquierda en el CRM. Sin imagen se muestra el logo de {brand.name}.
        </p>
      </div>

      <div className="mt-3 flex items-center gap-3">
        <div className="h-14 w-14 shrink-0 overflow-hidden rounded-[10px] border border-ink-200 bg-white">
          <img
            src={logoUrl ?? brandMarkSrc(brand)}
            alt={logoUrl ? "Imagen institucional actual" : `Logo de ${brand.name}`}
            className="h-full w-full object-contain"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(",")}
            onChange={handleFile}
            className="hidden"
            aria-label="Seleccionar imagen institucional"
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-[13px] font-medium text-ink-900 disabled:opacity-40"
          >
            {isUploading ? "Subiendo…" : logoUrl ? "Cambiar imagen" : "Subir imagen"}
          </button>
          {logoUrl && (
            <button
              type="button"
              disabled={busy}
              onClick={onRemove}
              className="rounded-lg px-3 py-1.5 text-[13px] font-medium text-ink-500 hover:text-red-500 disabled:opacity-40"
            >
              {isRemoving ? "Quitando…" : "Quitar imagen"}
            </button>
          )}
        </div>
      </div>

      <p className="mt-2 text-[11px] text-ink-500">
        PNG, JPG o WEBP
        {maxSizeMb !== undefined && ` · máximo ${maxSizeMb} MB`}. Se guarda al instante.
      </p>
      {error && <p className="mt-1 text-[11px] text-red-500">{error}</p>}
    </div>
  );
}
